//
//  src/components/landing/Landing.js
//
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Slider0 from "../sliders/SliderStudio";
import Video1  from "../videos/Video1";
import './Landing.css'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faAt
} from '@fortawesome/free-solid-svg-icons'
import {
    faFacebook,
    faInstagram
} from '@fortawesome/free-brands-svg-icons'
function importAll(r) { return r.keys().map(r); }
const polaroidImages = importAll(require.context('../../images/polaroids/', false, /\.(webp)$/));

function shuffleArray(items) {
    const arr = [...items];
    for (let i = arr.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
}

//
//  landing [social-icon]
//
const LandingSocialIcon = (props) => (
    <div className="social">
        <a href={props.url} aria-label={props.label || props.url}>
            <FontAwesomeIcon className="fa" icon={ props.icon } />
        </a>
    </div>
)


//
//  landing [testimonial-slider] — authors: first name + last initial only
//
const TESTIMONIALS_SOURCE = [
    {
        quote:
            "Jeff is super professional and an extremely talented artist who helps bring your dream tattoo to life. He helps with his expertise and listens to what you want with your piece. Jeff also makes you feel super comfortable, highly recommend.",
        author: "Rachael A."
    },
    {
        quote:
            "Jeff is an extremely professional, passionate and skilled artist with a keen and unique eye for detail, I've trusted Jeff with full creative freedom and ideas with my sleeve work and I couldn't be happier! I would highly recommend going to Jeff for any tattoo requirements, brilliant results from a ripper bloke!",
        author: "Harry V."
    },
    {
        quote:
            "I wanted a piece that reflected strength hope and courage. I got in touch with Jeff and not only did he understand what I wanted he exceeded my expectations. I can't recommend Jeff highly enough, the detail he went into was out of this world. Thanks again for making this happen.",
        author: "Nick S."
    },
    {
        quote:
            "I had 3 back to back sessions for my first arm sleeve and it was seamless from consult to final session. Comfortable clean studio and Jeff is absolutely lovely, chill and professional. He designed my 10+ year old idea into a masterpiece. Smooth flow and executed beautifully.",
        author: "Aisha D."
    },
    {
        quote:
            "Jeff is a remarkably talented artist whose work speaks for itself. Beyond his skill, he is great to deal with, honest with his input and made the entire experience fantastic for me.",
        author: "Nicki A."
    },
    {
        quote:
            "I have worked with Jeff for majority of all my tattoos and never disappoints. His freehand sketches is unreal and realistic, also a great guy to have a conversation with to distract you from the pain.",
        author: "Sam V."
    },
    {
        quote:
            "Trusted Jeff for several years now. Super reliable and top quality work always. Highly recommend.",
        author: "Stephen F."
    }
];

const TestimonialSlider = () => {
    const [testimonials] = React.useState(() => shuffleArray(TESTIMONIALS_SOURCE));
    const [currentIndex, setCurrentIndex] = React.useState(0);

    React.useEffect(() => {
        const interval = setInterval(() => {
            setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
        }, 16000);
        return () => clearInterval(interval);
    }, [testimonials.length]);

    const current = testimonials[currentIndex];

    return (
        <div className="testimonial-slider">
            <div className="testimonial-slide" key={currentIndex}>
                <p className="quote">"{current.quote}"</p>
                <p className="author">- {current.author}</p>
            </div>
            <div className="testimonial-dots">
                {testimonials.map((t, index) => (
                    <span
                        key={t.author + String(index)}
                        className={`dot ${index === currentIndex ? 'active' : ''}`}
                        onClick={() => setCurrentIndex(index)}
                        role="button"
                        tabIndex={0}
                        aria-label={`Show testimonial ${index + 1}`}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setCurrentIndex(index);
                            }
                        }}
                    ></span>
                ))}
            </div>
        </div>
    );
};


//
//  landing [polaroid-stack]
//
const PolaroidStack = () => {
    const navigate = useNavigate();
    const [selectedPolaroid, setSelectedPolaroid] = React.useState(null);
    const [hoveredPolaroid, setHoveredPolaroid] = React.useState(null);
    const [touchStart, setTouchStart] = React.useState(null);
    const [touchEnd, setTouchEnd] = React.useState(null);
    const [isSwiping, setIsSwiping] = React.useState(false);
    const [isCentered, setIsCentered] = React.useState(false);
    const hoverTimeoutRef = React.useRef(null);
    const stackRef = React.useRef(null);

    const minSwipeDistance = 50;
    const totalPolaroids = polaroidImages.length;

    const handlePolaroidClick = (index, e) => {
        if (isSwiping) {
            e.preventDefault();
            return;
        }
        
        if (selectedPolaroid === index) {
            navigate('/gallery/');
            setTimeout(() => {
                window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                });
            }, 100);
        } else {
            setSelectedPolaroid(index);
        }
    };

    const handleMouseEnter = (index) => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
        }
        setHoveredPolaroid(index);
    };

    const handleMouseLeave = () => {
        hoverTimeoutRef.current = setTimeout(() => {
            setHoveredPolaroid(null);
        }, 100);
    };

    React.useEffect(() => {
        return () => {
            if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current);
            }
        };
    }, []);

    React.useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => setIsCentered(entry.isIntersecting),
            { rootMargin: "-30% 0px -30% 0px", threshold: 0 }
        );
        if (stackRef.current) observer.observe(stackRef.current);
        return () => observer.disconnect();
    }, []);

    const onTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
        setIsSwiping(false);
    };

    const onTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
        if (touchStart && Math.abs(e.targetTouches[0].clientX - touchStart) > 10) {
            setIsSwiping(true);
        }
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) {
            setIsSwiping(false);
            return;
        }

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe || isRightSwipe) {
            if (selectedPolaroid === null) {
                setSelectedPolaroid(1);
            } else {
                if (isLeftSwipe) {
                    setSelectedPolaroid(selectedPolaroid < totalPolaroids ? selectedPolaroid + 1 : 1);
                } else if (isRightSwipe) {
                    setSelectedPolaroid(selectedPolaroid > 1 ? selectedPolaroid - 1 : totalPolaroids);
                }
            }
        }
        
        setTimeout(() => setIsSwiping(false), 100);
    };

    const isActive = (index) => {
        return selectedPolaroid === index || hoveredPolaroid === index;
    };

    return (
        <div
            ref={stackRef}
            className={`polaroid-stack${isCentered ? ' centered' : ''}`}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
        >
            {polaroidImages.map((src, i) => (
                <div
                    key={i}
                    className={`polaroid polaroid-${i + 1} ${isActive(i + 1) ? 'active' : ''}`}
                    onClick={(e) => handlePolaroidClick(i + 1, e)}
                    onMouseEnter={() => handleMouseEnter(i + 1)}
                    onMouseLeave={handleMouseLeave}
                >
                    <img src={src} alt="Tattoo artwork" className="polaroid-image" />
                </div>
            ))}
        </div>
    );
}


//
//  landing [handleContactClick]
//
const handleContactClick = (navigate) => {
    navigate('/contact/');
    setTimeout(() => {
        window.scrollTo({
            top: 0,
            behavior: 'instant'
        });
    }, 100);
};


//
//  landing [desktop]
//
const LandingDesktop = (props) => {
    const navigate = useNavigate();

    return (
        <>
            <div className="landing desktop">
                <div className="flex-container">
                    <div className="flex-item-left">
                        <div className="title">
                            <h4><b>Realistic tattoos that reflect who you are, made with attention and heart.</b></h4>
                        </div>
                        <div className="brief">
                            <div className="text">
                                <p>With a focus on colour and black & grey realism, I bring a fine-art approach to tattooing.</p>
                                <p>Every piece is designed with structure, balance, and longevity in mind, detailed enough to impress today, and strong enough to last.</p>
                                <p>Ready to bring your vision to life? Let's create something meaningful together.</p>
                            </div>
                            <div className="link">
                                <button className="contact" onClick={() => handleContactClick(navigate)}>
                                    <b>Get In Touch</b>
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="flex-item-right">
                        <div className="slider-0">
                            <Video1 />
                        </div>
                        <div className="bar">
                        </div>
                        <div className="text">
                            <h5><b>Realistic Ink, Timeless Art.</b></h5>
                        </div>
                    </div>
                </div>

                {/* Specialties strip */}
                <section className="landing-section specialties-section">
                    <div className="landing-section-inner">
                        <h2 className="landing-section-title">What I Specialise In</h2>
                        <p className="landing-section-subtitle">Different styles, same standard. Every piece is custom, considered, and built to last.</p>
                        <div className="specialties-grid">
                            <div className="specialty-card">
                                <div className="specialty-icon" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="9" />
                                        <circle cx="12" cy="12" r="5" />
                                        <circle cx="12" cy="12" r="1.5" fill="currentColor" />
                                    </svg>
                                </div>
                                <h3>Colour Realism</h3>
                                <p>Vivid portraits, animals, nature, and detail work. Painterly depth that holds its punch as it heals.</p>
                            </div>
                            <div className="specialty-card">
                                <div className="specialty-icon" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M12 2v20" />
                                        <path d="M4 12h16" />
                                        <path d="M6 6l12 12" />
                                        <path d="M18 6L6 18" />
                                    </svg>
                                </div>
                                <h3>Black &amp; Grey</h3>
                                <p>Photoreal black and grey with soft gradients and crisp contrast. Light, shadow, and patience.</p>
                            </div>
                            <div className="specialty-card">
                                <div className="specialty-icon" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M3 17l6-6 4 4 8-8" />
                                        <path d="M14 7h7v7" />
                                    </svg>
                                </div>
                                <h3>Custom Design</h3>
                                <p>From first sketch to finished piece. We work the idea together until it sits right.</p>
                            </div>
                            <div className="specialty-card">
                                <div className="specialty-icon" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21l8.84-8.61a5.5 5.5 0 0 0 0-7.78Z" />
                                    </svg>
                                </div>
                                <h3>Cover-ups &amp; Touch-ups</h3>
                                <p>Reworking old ink, refreshing faded pieces, and turning regrets into something you're proud of.</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Process strip */}
                <section className="landing-section process-section">
                    <div className="landing-section-inner">
                        <h2 className="landing-section-title">How It Works</h2>
                        <p className="landing-section-subtitle">A simple four-step path from idea to finished tattoo.</p>
                        <ol className="process-steps">
                            <li className="process-step">
                                <span className="process-step-num">01</span>
                                <div>
                                    <h3>Reach Out</h3>
                                    <p>Send through your idea, references, and any details. The more you share, the better I can plan.</p>
                                </div>
                            </li>
                            <li className="process-step">
                                <span className="process-step-num">02</span>
                                <div>
                                    <h3>Consultation</h3>
                                    <p>We talk through the concept, placement, size, and budget. Free of charge, no pressure to book.</p>
                                </div>
                            </li>
                            <li className="process-step">
                                <span className="process-step-num">03</span>
                                <div>
                                    <h3>Design</h3>
                                    <p>I draw up a custom design tailored to you. Revisions until it feels exactly right.</p>
                                </div>
                            </li>
                            <li className="process-step">
                                <span className="process-step-num">04</span>
                                <div>
                                    <h3>Session</h3>
                                    <p>Clean, comfortable, focused. We bring it to life in the chair, one careful pass at a time.</p>
                                </div>
                            </li>
                        </ol>
                    </div>
                </section>

                {/* Testimonials strip */}
                <section className="landing-section testimonials-desktop-section">
                    <div className="landing-section-inner">
                        <h2 className="landing-section-title">Kind Words From the Chair</h2>
                        <p className="landing-section-subtitle">Real notes from people who've spent time under the needle.</p>
                        <TestimonialSlider />
                    </div>
                </section>

                {/* Bottom CTA */}
                <section className="landing-section landing-cta-section">
                    <div className="landing-section-inner landing-cta-inner">
                        <h2 className="landing-cta-title">Ready to plan your next piece?</h2>
                        <p className="landing-cta-text">Every tattoo starts with a conversation. Share your idea and we'll go from there.</p>
                        <button className="landing-cta-btn" onClick={() => handleContactClick(navigate)}>
                            <b>Book a Free Consultation</b>
                        </button>
                    </div>
                </section>
            </div>
        </>
    );
};


//
//  landing [mobile]
//
const LandingMobile = (props) => {
    const navigate = useNavigate();

    return (
        <>
            <div className="landing mobile">
                <div id="flex-container-top" className="flex-container">
                    <div className="flex-item-left profile-picture slider2">
                        <Video1 />
                    </div>
                    <div className="flex-item-right">
                        <div className="text-1 valign-wrapper">
                            <div className="center-align">
                                <h4>Realistic <b>Ink</b></h4>
                                <p>With a focus on colour and black & grey realism, I bring a fine-art approach to tattooing. Every piece is designed with structure, balance, and longevity in mind, detailed enough to impress today, and strong enough to last.</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div id="flex-container-middle" className="flex-container">
                    <div className="flex-item-left profile-picture polaroid-container">
                        <PolaroidStack />
                    </div>
                    <div className="flex-item-right white">
                        <div className="text-1 valign-wrapper">
                            <div className="center-align testimonials-container">
                                <TestimonialSlider />
                            </div>
                        </div>
                    </div>
                </div>
                <div id="flex-container-bot" className="flex-container">
                    <div className="flex-item-left profile-picture slider1">
                        <Slider0 />
                    </div>
                    <div className="flex-item-right">
                        <div className="text-1 valign-wrapper">
                            <div className="center-align">
                                <div className="mobile-cta-section">
                                    <h4 className="cta-heading"><b>Ready to Start Your Journey?</b></h4>
                                    <p className="cta-description">Every tattoo begins with a conversation. Share your ideas and let's turn your vision into a piece you'll be proud to wear for life.</p>
                                    <button className="contact-cta" onClick={() => handleContactClick(navigate)}>
                                        <b>Book Free Consultation</b>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div id="flex-container-social" className="flex-container">
                    <div className="flex-item-full">
                        <div className="text-1 valign-wrapper">
                            <div className="center-align">
                                <br />
                                <h4><b>Connect</b> & <b>Share</b></h4>
                                <p>Check out my latest work and behind-the-scenes content on Instagram and Facebook. Have questions or ready to book? Drop me a message through any of the channels below.</p>
                                <div className="row socials">
                                    <LandingSocialIcon icon={faAt}        url="/contact/"                                label="Contact via websote" />
                                    <LandingSocialIcon icon={faFacebook}  url="https://www.facebook.com/JeffTattooer/"   label="Visit our Facebook page" />
                                    <LandingSocialIcon icon={faInstagram} url="https://www.instagram.com/jeff_tattooer/" label="Visit our Instagram page" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}


//
//  landing [responsive]
//
export default function Landing() {

    // viewport[s].
    const [isDesktop, setDesktop] = useState( window.innerWidth  >  1000 );
    const [isMobile,  setMobile]  = useState( window.innerWidth  <= 1000 );

    // update container height.
    const updateContainerHeight = () => {
        let containerHeight = document.getElementById("flex-container-top");
        if( containerHeight !== null ){
            document.getElementById("flex-container-bot").style.height = containerHeight.clientHeight + "px"
        }
    }

    // update media viewport.
    const updateMedia = () => {
        setDesktop( window.innerWidth  >  1000 );
        setMobile(  window.innerWidth  <= 1000 );
        updateContainerHeight();
    };

    // use resize.
    useEffect(() => {
        window.addEventListener("resize", updateMedia);
        setTimeout(function() { updateContainerHeight() },  50)
        setTimeout(function() { updateContainerHeight() }, 100)
        setTimeout(function() { updateContainerHeight() }, 400)
        setTimeout(function() { updateContainerHeight() }, 700)
        return () => window.removeEventListener("resize", updateMedia);
    });

    // return
    return (
        <>
            { isDesktop && <LandingDesktop /> }
            { isMobile  && <LandingMobile  /> }
        </>
    );

}
